import {
  APIProvider,
  InfoWindow,
  Map,
  AdvancedMarker,
} from "@vis.gl/react-google-maps";
import { GOOGLE_API_KEY, GOOGLE_MAP_ID } from "constants/common";
import { IDriverLocation, ILocation } from "models/dispatch";
import DriverInfo from "./DriverInfo";
import { Tag, Button } from "antd";
import { SyncOutlined } from "@ant-design/icons";

interface IProps {
  currentLocation: ILocation;
  driverLocation: IDriverLocation[];
  handleCloseDriverInfo: (id: string) => void;
  handleShowDriverInfo: (id: string) => void;
  showDriverInfo: {
    [key: string]: boolean;
  };
  onRefreshDrivers?: () => void;
  isRefreshingDrivers?: boolean;
  excludeStuckDrivers?: boolean;
  onToggleExcludeStuck?: () => void;
}

export default function FleetMap({
  currentLocation,
  driverLocation,
  handleCloseDriverInfo,
  handleShowDriverInfo,
  showDriverInfo,
  onRefreshDrivers,
  isRefreshingDrivers = false,
  excludeStuckDrivers = false,
  onToggleExcludeStuck,
}: IProps) {
  return (
    <div className="w-3/4 bg-white p-4 shadow-lg flex flex-col ">
      <div className="flex justify-between items-center p-4">
        <p className="font-bold" style={{ fontSize: "24px" }}>
          Active Fleets
        </p>
        <div className="flex gap-2">
          <Button
            type={excludeStuckDrivers ? "default" : "primary"}
            onClick={onToggleExcludeStuck}
            size="small"
            className={excludeStuckDrivers ? "bg-green-100 border-green-300 text-green-700" : ""}
          >
            {excludeStuckDrivers ? "Show All Drivers" : "Show Active Only"}
          </Button>
          <Button
            type="primary"
            icon={<SyncOutlined spin={isRefreshingDrivers} />}
            onClick={onRefreshDrivers}
            loading={isRefreshingDrivers}
            size="small"
          >
            {isRefreshingDrivers ? "Refreshing..." : "Refresh Drivers"}
          </Button>
        </div>
      </div>
      <APIProvider apiKey={GOOGLE_API_KEY}>
        <Map
          defaultZoom={14}
          defaultCenter={currentLocation}
          mapId={GOOGLE_MAP_ID}
        >
          {driverLocation.map((driver, key) => {
            const stuckLevel = (driver as any).stuckLevel || 'active';
            const isCaution = stuckLevel === 'caution';
            const isWarning = stuckLevel === 'warning';
            const isCritical = stuckLevel === 'critical';
            return (
              <div key={key}>
                <AdvancedMarker
                  position={{ lat: driver.latitude, lng: driver.longitude }}
                  onClick={() => handleShowDriverInfo(driver.id)}
                >
                  {driver.fleet.fleet_model.image ? (
                    <img
                      src={driver.fleet.fleet_model.image}
                      alt="Driver vehicle"
                      style={{
                        width: '50px',
                        height: '40px',
                        objectFit: 'contain',
                        cursor: 'pointer',
                        transform: 'translate(-50%, -50%)',
                      }}
                      title={`Driver: ${driver.id || 'Unknown'}`}
                      crossOrigin="anonymous"
                      onError={(e) => {
                        // Fallback to a default vehicle icon if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA1MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjQwIiByeD0iNCIgZmlsbD0iIzMzMzMzMyIvPgo8cGF0aCBkPSJNMjUgMTJMMzAgMjBIMjBMMjUgMTJaTTMwIDI1SDE1VjMwSDE1VjI1Wk0yNSAxN0gzME0yMCAxN0gyNVoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=';
                      }}
                    />
                  ) : (
                    // Placeholder vehicle icon
                    <div
                      style={{
                        width: '50px',
                        height: '40px',
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '20px',
                      }}
                      title="No vehicle image available"
                    >
                      🚗
                    </div>
                  )}
                </AdvancedMarker>
                {/* Status indicators based on activity level */}
                {stuckLevel === 'active' && (
                  <AdvancedMarker
                    position={{ lat: driver.latitude, lng: driver.longitude }}
                    onClick={() => handleShowDriverInfo(driver.id)}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: 'rgba(34, 197, 94, 0.8)',
                        borderRadius: '50%',
                        border: '2px solid #16a34a',
                        cursor: 'pointer',
                        transform: 'translate(-50%, -50%)',
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                      title="✅ Active Driver"
                    >
                      ✓
                    </div>
                  </AdvancedMarker>
                )}

                {/* Caution indicator for drivers inactive >5 minutes */}
                {isCaution && (
                  <AdvancedMarker
                    position={{ lat: driver.latitude, lng: driver.longitude }}
                    onClick={() => handleShowDriverInfo(driver.id)}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'rgba(251, 146, 60, 0.8)',
                        borderRadius: '50%',
                        border: '2px solid #ea580c',
                        cursor: 'pointer',
                        transform: 'translate(-50%, -50%)',
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                      title="⏰ Location Update Pending"
                    >
                      ⏰
                    </div>
                  </AdvancedMarker>
                )}

                {/* Warning indicator for drivers inactive >1 hour */}
                {isWarning && (
                  <AdvancedMarker
                    position={{ lat: driver.latitude, lng: driver.longitude }}
                    onClick={() => handleShowDriverInfo(driver.id)}
                  >
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: 'rgba(245, 158, 11, 0.8)',
                        borderRadius: '50%',
                        border: '2px solid #d97706',
                        cursor: 'pointer',
                        transform: 'translate(-50%, -50%)',
                        position: 'absolute',
                        top: '-10px',
                        right: '-10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                      title="⚠️ Extended Inactivity"
                    >
                      ⚠️
                    </div>
                  </AdvancedMarker>
                )}

                {/* Critical indicator for drivers inactive >24 hours */}
                {isCritical && (
                  <AdvancedMarker
                    position={{ lat: driver.latitude, lng: driver.longitude }}
                    onClick={() => handleShowDriverInfo(driver.id)}
                  >
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        backgroundColor: 'rgba(239, 68, 68, 0.8)',
                        borderRadius: '50%',
                        border: '3px solid #dc2626',
                        cursor: 'pointer',
                        transform: 'translate(-50%, -50%)',
                        position: 'absolute',
                        top: '-12px',
                        right: '-12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                      title="🚨 Critical: Multiple Days Inactive"
                    >
                      🚨
                    </div>
                  </AdvancedMarker>
                )}
                {showDriverInfo[driver.id] && (
                  <InfoWindow
                    key={`${driver.id}-info`}
                    position={{
                      lat: driver.latitude,
                      lng: driver.longitude,
                    }}
                    onCloseClick={() => handleCloseDriverInfo(driver.id)}
                  >
                    <div>
                      <DriverInfo driver={driver} />
                      {stuckLevel === 'active' && (
                        <div className="mt-2">
                          <Tag color="green" className="font-semibold">
                            ✅ Active in Service Area - Location updated
                          </Tag>
                        </div>
                      )}
                      {isCaution && (
                        <div className="mt-2">
                          <Tag color="orange" className="font-semibold">
                            📍 Active in Service Area - Location update pending
                          </Tag>
                        </div>
                      )}
                      {isWarning && (
                        <div className="mt-2">
                          <Tag color="orange" className="font-semibold">
                            ⚠️ Extended Inactivity - Check driver status
                          </Tag>
                        </div>
                      )}
                      {isCritical && (
                        <div className="mt-2">
                          <Tag color="red" className="font-semibold">
                            🚨 Critical: Multiple Days Inactive - Requires attention
                          </Tag>
                        </div>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </div>
            );
          })}
        </Map>
      </APIProvider>
    </div>
  );
}
